import React from 'react'
import styled from 'styled-components'
import { Tooltip } from '../../common'
import { useSettings, Settings } from '../../providers'
import { CurrencyValue } from '../../model'
import { getFirstValueWithSlippage, getSecondValueWithSlippage } from './SlippageMath'
import { Colors } from '../../constants'
import { getSwapFeeDisplay, getPriceImpactDisplay, getSlippageToleranceDisplay } from './getSwapDisplays'

interface Props {
  controlled: 'first' | 'second'
  firstValue: CurrencyValue
  secondValue: CurrencyValue
  swapFee: CurrencyValue
  slippagePercent: string
}

export function SwapIntoTooltip({ controlled, firstValue, secondValue, swapFee, slippagePercent }: Props) {
  const { settings } = useSettings()

  const { buySellLimitLabel, buySellLimitAmount } = calculateBuySellLimits(
    settings,
    controlled,
    firstValue,
    secondValue
  )

  const swapFeeDisplay = getSwapFeeDisplay(swapFee)
  const priceImpactDisplay = getPriceImpactDisplay(slippagePercent)
  const slippageToleranceDisplay = getSlippageToleranceDisplay(settings.slippageTolerance)

  return (
    <TooltipStyled>
      <ul>
        <li>
          <Label>Fees</Label>
          <Value>{swapFeeDisplay}</Value>
        </li>
        <li>
          <Label>Price Impact</Label>
          <Value>{priceImpactDisplay}</Value>
        </li>
        <li>
          <Label>{buySellLimitLabel}</Label>
          <Value>{buySellLimitAmount}</Value>
        </li>
        <li>
          <Label>Slippage Tolerance</Label>
          <Value>{slippageToleranceDisplay}</Value>
        </li>
      </ul>
    </TooltipStyled>
  )
}

function calculateBuySellLimits(
  settings: Settings,
  controlled: 'first' | 'second',
  firstValue: CurrencyValue,
  secondValue: CurrencyValue
) {
  const firstControlled = controlled === 'first'
  const firstValueWithSlippage = getFirstValueWithSlippage(controlled, firstValue, settings).formatWithSymbol()
  const secondValueWithSlippage = getSecondValueWithSlippage(controlled, secondValue, settings).formatWithSymbol()

  return {
    buySellLimitLabel: firstControlled ? 'Minimum Received' : 'Maximum Sent',
    buySellLimitAmount: firstControlled ? secondValueWithSlippage : firstValueWithSlippage,
  }
}

const TooltipStyled = styled(Tooltip)`
  width: 220px;

  ul {
    margin: 0;
    padding: 0;
    width: 100%;
    list-style: none;
  }

  li {
    display: flex;
    justify-content: space-between;
  }
`

const Label = styled.span`
  font-size: 11px;
  color: ${Colors.DarkerGray};
`

const Value = styled.span`
  font-size: 11px;
  font-weight: bold;
`
